import { useState, useCallback, useRef } from "react";
import api from "../services/api";
import { lerProdutoAtual } from "./produtoAtualStorage";

// PO1-3508: a tela atual (pathname + search), cortada em 300 caracteres como o
// backend_hub faz. Quem abre o chat em tela cheia vindo de outra tela passa a
// origem em `opcoes.tela`; senão vale a URL de onde o chat foi aberto.
export function telaAtual(tela) {
  const base = tela || (typeof window !== "undefined" && window.location ? window.location.pathname + window.location.search : "");
  return String(base || "").slice(0, 300);
}

const useAutopilotChat = (empresaId, opcoes) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sessionId = useRef(crypto.randomUUID());
  const requestId = useRef(0);
  const empresaIdRef = useRef(empresaId);
  empresaIdRef.current = empresaId;
  const telaRef = useRef(opcoes && opcoes.tela);
  telaRef.current = opcoes && opcoes.tela;

  const sendMessage = useCallback(async (text, { displayText } = {}) => {
    if (!text || !text.trim()) return;

    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    const currentSessionId = sessionId.current;
    const userMessage = { role: "user", content: (displayText || text).trim(), ts: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const body = { message: text.trim(), sessionId: currentSessionId };
      if (empresaIdRef.current) body.embarcadorId = empresaIdRef.current;
      // PO1-3508: produto ativo e tela vão no contexto do Autopilot (o
      // backend_hub valida o produto contra os do usuário e corta a tela).
      const produto = lerProdutoAtual();
      if (produto) body.produto = produto;
      const tela = telaAtual(telaRef.current);
      if (tela) body.tela = tela;
      const response = await api.post("/autopilot/chat", body);
      if (requestId.current !== currentRequestId || sessionId.current !== currentSessionId) return;
      const assistantMessage = {
        role: "assistant",
        content: response.data.answer || "",
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      if (requestId.current !== currentRequestId || sessionId.current !== currentSessionId) return;
      setError("Não foi possível obter resposta. Tente novamente.");
    } finally {
      if (requestId.current === currentRequestId) {
        setLoading(false);
      }
    }
  }, []);

  const clearMessages = useCallback(() => {
    requestId.current += 1;
    sessionId.current = crypto.randomUUID();
    setMessages([]);
    setError(null);
    setLoading(false);
  }, []);

  const resetWithSession = useCallback((newSessionId) => {
    requestId.current += 1;
    sessionId.current = newSessionId || crypto.randomUUID();
    setMessages([]);
    setError(null);
    setLoading(false);
  }, []);

  const injectAssistantMessage = useCallback((content) => {
    setMessages([{ role: "assistant", content, ts: Date.now() }]);
  }, []);

  const injectConversation = useCallback((userContent, assistantContent) => {
    setMessages([
      { role: "user", content: userContent, ts: Date.now() },
      { role: "assistant", content: assistantContent, ts: Date.now() + 1 },
    ]);
  }, []);

  return { messages, loading, error, sendMessage, clearMessages, resetWithSession, injectAssistantMessage, injectConversation };
};

export default useAutopilotChat;
